/**
 * Constants - Game constants and configuration values
 * Centralized location for all magic numbers and configuration
 */

// Time and Physics
export const FIXED_DELTA_TIME = 1 / 60; // Target: 60 FPS equivalent
export const MAX_FRAME_SKIP = 3; // Maximum physics steps per frame to prevent spiral of death

// Player Movement
export const PLAYER_BASE_SPEED = 8.0; // Units per second
export const PLAYER_STOP_DISTANCE = 0.1; // Distance threshold to stop movement

// Ground and Environment
export const GROUND_RADIUS = 50; // Radius of the circular ground

// Cloud and Shadow Animation
export const SHADOW_TEXTURE_UV_SPEED_X = 0.5;
export const SHADOW_TEXTURE_UV_SPEED_Z = 0.3;
export const GROUND_SHADOW_TIME_MULTIPLIER = 0.05;
export const CLOUD_WORLD_MOVEMENT_SCALE = 40;
export const CLOUD_AREA_SIZE = 120;
export const CLOUD_HEIGHT = 25;
export const NUM_CLOUDS = 15;

// Fishing Holes Configuration
export const FISHING_HOLE_POSITIONS = [
  { x: -8, z: -5, name: 'Starter Pond' },
  { x: 5, z: 3, name: 'Meadow Pond' },
  { x: -3, z: 8, name: 'Garden Pond' },
  { x: 10, z: -8, name: 'Rocky Pond' },
  { x: -20, z: -15, name: 'Forest Pond' },
  { x: 18, z: 12, name: 'Sunny Pond' },
  { x: -15, z: 22, name: 'Hidden Pond' },
  { x: 25, z: -20, name: 'Deep Pond' }
];

// Fishing Mechanics
export const FISHING_BITE_DELAY_MIN = 2000; // Minimum wait for fish bite (ms)
export const FISHING_BITE_DELAY_MAX = 5000; // Maximum wait for fish bite (ms)
export const FISHING_APPROACH_DISTANCE = 3; // Distance to stand from fishing hole

// Minigame Configuration
export const MINIGAME_BASE_SPEED = 100; // Base ball speed (pixels/second)
export const MINIGAME_CHANCES = 3; // Number of attempts per catch

// Difficulty settings per rarity
export const MINIGAME_DIFFICULTY = {
  common: { speedMultiplier: 1.2, zoneWidth: 22 },
  uncommon: { speedMultiplier: 1.6, zoneWidth: 18 },
  rare: { speedMultiplier: 2.2, zoneWidth: 14 },
  legendary: { speedMultiplier: 3.0, zoneWidth: 10 }
};

// Fish Rarity Spawn Rates
export const FISH_SPAWN_RATES = {
  legendary: 0.05, // 5%
  rare: 0.20, // 20%
  uncommon: 0.35, // 35%
  common: 0.40 // 40%
};

// House Configuration
export const HOUSE_ENTER_DISTANCE = 2; // Distance to trigger house entry
export const HOUSE_POSITION = { x: -35, z: 25 };
export const TRANSITION_DURATION = 1500; // Iris wipe duration (ms)

// Camera Configuration
export const CAMERA_FOV = 75;
export const CAMERA_NEAR = 0.1;
export const CAMERA_FAR = 1000;
export const CAMERA_POSITION = { x: 0, y: 15, z: 25 };
export const CAMERA_HOUSE_POSITION = { x: 5, y: 4, z: 6 };

// Lighting Configuration
export const AMBIENT_LIGHT_INTENSITY = 1.0;
export const DIRECTIONAL_LIGHT_INTENSITY = 1.2;
export const DIRECTIONAL_LIGHT_POSITION = { x: 30, y: 50, z: 30 };

// Colors (hex values)
export const COLORS = {
  sky: {
    top: 0x87ceeb,
    bottom: 0xffd699
  },
  water: {
    light: 0x5599dd,
    mid: 0x3377bb,
    dark: 0x225588
  },
  ground: {
    grass: 0xa2c49d,
    sand: 0xddd4a8,
    rock: 0x888888
  },
  player: 0xff6b35,
  trees: {
    trunk: 0x8b4513,
    foliage: 0xa2c49d
  },
  notification: {
    common: 0x2196f3,
    uncommon: 0x4caf50,
    rare: 0xffc107,
    legendary: 0x9c27b0
  }
};

// UI Configuration
export const NOTIFICATION_DURATION = 2000; // Default notification duration (ms)
export const FISH_DISPLAY_DURATION = 3000; // How long caught fish is shown (ms)

// Vegetation Counts
export const VEGETATION = {
  trees: 16,
  rocks: 12,
  bushes: 10,
  leavesPerTree: { min: 20, max: 35 }
};

// Achievement Thresholds
export const ACHIEVEMENT_THRESHOLDS = {
  fishCaught: [1, 10, 25, 50, 100],
  speciesDiscovered: [1, 5, 9, 12, 18],
  consecutiveCatches: [3, 5, 10]
};

// Progression Thresholds
export const PROGRESSION_THRESHOLDS = {
  ponds_tier2: { fishCaught: 20 }, // Unlock ponds 4-5
  ponds_tier3: { speciesDiscovered: 12 }, // Unlock ponds 6-7
  rod_level2: { fishCaught: 25 },
  rod_level3: { fishCaught: 50 },
  rod_level4: { speciesDiscovered: 10 }
};

// Rod Bonuses
export const ROD_BONUSES = {
  1: { catchWindowBonus: 0, rareBonus: 0 },
  2: { catchWindowBonus: 0.10, rareBonus: 0 },
  3: { catchWindowBonus: 0.20, rareBonus: 0 },
  4: { catchWindowBonus: 0.30, rareBonus: 0.05 }
};

// LocalStorage Keys
export const STORAGE_KEYS = {
  GAME_SAVE: 'fishgame_savedata',
  SETTINGS: 'fishgame_settings',
  ACHIEVEMENTS: 'fishgame_achievements',
  PROGRESSION: 'fishgame_progression',
  TUTORIAL_COMPLETE: 'fishgame_tutorial_complete',
  WIND_EFFECT: 'windEffectSettings',
  WIND_ENABLED: 'windEffectEnabled'
};
